import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Mail, Save, CheckCircle, AlertCircle,
  Shield, Tag, MapPin, Link as LinkIcon, Swords
} from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { authAPI } from '../api';
import './ProfilePage.css';

export default function ProfilePage() {
  const { user, updateProfile, loginWithToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetup = searchParams.get('setup') === '1';

  const [profile, setProfile] = useState({ displayName: '', email: '' });
  const [riotLinked, setRiotLinked] = useState(null);
  const [riotForm, setRiotForm] = useState({ riotId: '', tagLine: '', region: 'euw1' });

  const [loading, setLoading] = useState(false);
  const [riotLoading, setRiotLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [riotError, setRiotError] = useState('');
  const [riotSuccess, setRiotSuccess] = useState('');

  const regions = [
    { code: 'euw1', name: '🇪🇺 Europe West' },
    { code: 'eun1', name: '🇪🇺 Europe Nordic & East' },
    { code: 'tr1',  name: '🇹🇷 Turkey' },
    { code: 'ru',   name: '🇷🇺 Russia' },
    { code: 'na1',  name: '🇺🇸 North America' },
    { code: 'br1',  name: '🇧🇷 Brazil' },
    { code: 'la1',  name: '🇲🇽 Latin America North' },
    { code: 'la2',  name: '🇨🇱 Latin America South' },
    { code: 'kr',   name: '🇰🇷 Korea' },
    { code: 'jp1',  name: '🇯🇵 Japan' },
  ];

  useEffect(() => {
    authAPI.getCurrentUser()
      .then(res => {
        const u = res.data;
        setProfile({
          displayName: u.displayName || u.display_name || '',
          email: (u.email && !u.email.endsWith('@discord.lol') && !u.email.endsWith('@riot.lol'))
            ? u.email : '',
        });
        if (u.riotId || u.riot_id) {
          setRiotLinked({
            riotId:  u.riotId  || u.riot_id,
            tagLine: u.tagLine || u.tag_line,
            tier:    u.tier,
            rank:    u.rank,
            level:   u.level,
            region:  u.region,
          });
        }
      })
      .catch(() => {
        setProfile({
          displayName: user?.displayName || '',
          email: (user?.email && !user.email.endsWith('@discord.lol') && !user.email.endsWith('@riot.lol'))
            ? user.email : '',
        });
        if (user?.riotId) {
          setRiotLinked({
            riotId:  user.riotId,
            tagLine: user.tagLine,
            tier:    user.tier,
            rank:    user.rank,
            level:   user.level,
            region:  user.region,
          });
        }
      });
  }, [user]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const updates = {};
      if (profile.displayName.trim()) updates.displayName = profile.displayName.trim();
      if (profile.email.trim())       updates.email       = profile.email.trim();
      await updateProfile(updates);
      setSuccess('Profil mis à jour !');
      if (isSetup) setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkRiot = async (e) => {
    e.preventDefault();
    setRiotError(''); setRiotSuccess(''); setRiotLoading(true);
    try {
      if (!riotForm.riotId.trim())  throw new Error('Riot ID requis');
      if (!riotForm.tagLine.trim()) throw new Error('Tag requis');
      const res = await authAPI.linkRiot(
        riotForm.riotId.trim(),
        riotForm.tagLine.trim().toUpperCase(),
        riotForm.region
      );
      const updatedUser = res.data.user;
      setRiotLinked({
        riotId:  updatedUser.riotId,
        tagLine: updatedUser.tagLine,
        tier:    updatedUser.tier,
        rank:    updatedUser.rank,
        level:   updatedUser.level,
        region:  updatedUser.region,
      });
      const token = localStorage.getItem('token');
      if (token) loginWithToken(token, updatedUser);
      setRiotSuccess('Compte Riot lié avec succès !');
    } catch (err) {
      setRiotError(err.message || 'Erreur lors de la liaison Riot');
    } finally {
      setRiotLoading(false);
    }
  };

  const tierColor = (tier) => {
    const colors = {
      IRON: '#6b7280', BRONZE: '#b45309', SILVER: '#9ca3af',
      GOLD: '#d97706', PLATINUM: '#0d9488', EMERALD: '#059669',
      DIAMOND: '#6366f1', MASTER: '#9333ea', GRANDMASTER: '#dc2626',
      CHALLENGER: '#0ea5e9',
    };
    return colors[tier?.toUpperCase()] || '#9ca3af';
  };

  const initials = (profile.displayName || user?.displayName || user?.username || '?')
    .substring(0, 2).toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prf-page"
    >
      {/* ── Toast succès/erreur profil ── */}
      <AnimatePresence>
        {(success || error) && (
          <motion.div
            key={success || error}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`prf-toast ${success ? 'prf-toast--success' : 'prf-toast--error'}`}
          >
            {success
              ? <><CheckCircle size={16} /> {success}</>
              : <><AlertCircle size={16} /> {error}</>
            }
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════ HERO ══════════════ */}
      <div className="prf-hero">
        <div className="prf-hero__bg" />
        <div className="prf-hero__overlay" />
        <div className="prf-hero__orb prf-hero__orb--violet" />
        <div className="prf-hero__orb prf-hero__orb--rose" />
        <div className="prf-hero__orb prf-hero__orb--teal" />

        <div className="prf-hero__inner">
          <motion.div
            className="prf-hero__avatar"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 260 }}
          >
            <div className="prf-hero__avatar-ring" />
            <div className="prf-hero__avatar-placeholder">
              <span className="prf-hero__avatar-initials">{initials}</span>
            </div>
          </motion.div>

          <motion.div
            className="prf-hero__info"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="prf-hero__name-row">
              <h1 className="prf-hero__name">
                {profile.displayName || user?.displayName || 'Mon profil'}
              </h1>
              {isSetup && (
                <span className="prf-hero__setup-pill">Configuration</span>
              )}
            </div>

            {riotLinked ? (
              <div className="prf-hero__riot-row">
                <Shield size={14} className="prf-hero__riot-icon" />
                <span className="prf-hero__riot-id">{riotLinked.riotId}</span>
                <span className="prf-hero__riot-tag">#{riotLinked.tagLine}</span>
                {riotLinked.tier && (
                  <span
                    className="prf-hero__rank-pill"
                    style={{ '--tier-color': tierColor(riotLinked.tier) }}
                  >
                    {riotLinked.tier} {riotLinked.rank}
                  </span>
                )}
                {riotLinked.region && (
                  <span className="prf-hero__region-pill">
                    {riotLinked.region.toUpperCase()}
                  </span>
                )}
              </div>
            ) : (
              <div className="prf-hero__no-riot">
                <AlertCircle size={14} />
                Aucun compte Riot lié
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* ══════════════ LAYOUT ══════════════ */}
      <div className="prf-layout">

        {/* Carte : Infos générales */}
        <motion.div
          className="prf-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="prf-card__header">
            <div className="prf-card__icon-wrap">
              <User size={18} />
            </div>
            <div>
              <h2 className="prf-card__title">
                {isSetup ? 'Compléter le profil' : 'Informations'}
              </h2>
              <p className="prf-card__subtitle">
                {isSetup
                  ? 'Ajoutez votre email pour les notifications de tournois.'
                  : 'Nom affiché et adresse e-mail.'
                }
              </p>
            </div>
          </div>

          <form onSubmit={handleProfileSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="prf-displayName">Nom affiché</label>
              <div className="input-wrapper">
                <User size={20} />
                <input
                  id="prf-displayName"
                  type="text"
                  value={profile.displayName}
                  onChange={e => setProfile(f => ({ ...f, displayName: e.target.value }))}
                  placeholder="Votre pseudo affiché"
                  minLength={2}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="prf-email">
                Email
                {!profile.email && (
                  <span className="prf-hint-badge">non configuré</span>
                )}
              </label>
              <div className="input-wrapper">
                <Mail size={20} />
                <input
                  id="prf-email"
                  type="email"
                  value={profile.email}
                  onChange={e => setProfile(f => ({ ...f, email: e.target.value }))}
                  placeholder="votre@email.com"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="prf-form-actions">
              <button
                type="submit"
                disabled={loading}
                className="submit-button riot-submit prf-save-btn"
              >
                <Save size={18} />
                {loading ? 'Sauvegarde...' : 'Enregistrer'}
              </button>
              {isSetup && (
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => navigate('/dashboard')}
                  disabled={loading}
                >
                  Passer pour l'instant
                </button>
              )}
            </div>
          </form>
        </motion.div>

        {/* Carte : Liaison Riot */}
        <motion.div
          className="prf-card prf-riot-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="prf-card__header">
            <div className="prf-card__icon-wrap prf-card__icon-wrap--riot">
              <Shield size={18} />
            </div>
            <div>
              <h2 className="prf-card__title">Compte Riot</h2>
              <p className="prf-card__subtitle">Requis pour participer aux tournois.</p>
            </div>
          </div>

          {riotLinked ? (
            <div className="prf-riot-badge">
              <div className="prf-riot-badge__shimmer" />
              <div className="prf-riot-badge__top">
                <Shield size={16} className="prf-riot-badge__shield" />
                <span className="prf-riot-badge__id">{riotLinked.riotId}</span>
                <span className="prf-riot-badge__tag">#{riotLinked.tagLine}</span>
                <CheckCircle size={15} className="prf-riot-badge__check" />
              </div>
              {riotLinked.tier && (
                <div className="prf-riot-badge__stats">
                  <span
                    className="prf-riot-badge__rank"
                    style={{ color: tierColor(riotLinked.tier) }}
                  >
                    {riotLinked.tier} {riotLinked.rank}
                  </span>
                  {riotLinked.level && (
                    <span className="prf-riot-badge__meta">· Niv. {riotLinked.level}</span>
                  )}
                  {riotLinked.region && (
                    <span className="prf-riot-badge__meta">
                      · {riotLinked.region.toUpperCase()}
                    </span>
                  )}
                </div>
              )}
              <p className="prf-riot-badge__hint">
                Pour changer de compte, entre un nouveau Riot ID ci-dessous.
              </p>
            </div>
          ) : (
            <div className="prf-riot-warning">
              <AlertCircle size={16} />
              Aucun compte Riot lié — requis pour rejoindre les tournois.
            </div>
          )}

          <AnimatePresence>
            {riotSuccess && (
              <motion.div
                key="riot-success"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="prf-inline-feedback prf-inline-feedback--success"
              >
                <CheckCircle size={15} /> {riotSuccess}
              </motion.div>
            )}
            {riotError && (
              <motion.div
                key="riot-error"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="prf-inline-feedback prf-inline-feedback--error"
              >
                <AlertCircle size={15} /> {riotError}
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleLinkRiot} className="auth-form">
            <div className="riot-id-group">
              <div className="form-group" style={{ flex: 2 }}>
                <label htmlFor="prf-riotId">Nom d'invocateur</label>
                <div className="input-wrapper">
                  <Swords size={18} />
                  <input
                    id="prf-riotId"
                    type="text"
                    value={riotForm.riotId}
                    onChange={e => setRiotForm(f => ({ ...f, riotId: e.target.value }))}
                    placeholder="PlayerName"
                    disabled={riotLoading}
                  />
                </div>
              </div>
              <div className="riot-id-separator">#</div>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="prf-tagLine">Tag</label>
                <div className="input-wrapper">
                  <Tag size={18} />
                  <input
                    id="prf-tagLine"
                    type="text"
                    value={riotForm.tagLine}
                    onChange={e => setRiotForm(f => ({ ...f, tagLine: e.target.value.toUpperCase() }))}
                    placeholder="EUW"
                    maxLength={5}
                    disabled={riotLoading}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="prf-region">Région</label>
              <div className="input-wrapper">
                <MapPin size={18} />
                <select
                  id="prf-region"
                  value={riotForm.region}
                  onChange={e => setRiotForm(f => ({ ...f, region: e.target.value }))}
                  disabled={riotLoading}
                >
                  {regions.map(r => (
                    <option key={r.code} value={r.code}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={riotLoading}
              className="submit-button riot-submit prf-riot-submit-btn"
            >
              <LinkIcon size={18} />
              {riotLoading
                ? 'Vérification...'
                : riotLinked
                  ? 'Changer de compte Riot'
                  : 'Lier mon compte Riot'
              }
            </button>
          </form>
        </motion.div>

      </div>
    </motion.div>
  );
}
